import React, { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import {
  AppButton,
  StickyHeaderNavigation,
  AnimatedTextInput,
  PhotoInput,
  UpdateInfoModal,
  ChooseMessagingAppModal,
  UpdateEmailMethodModal,
} from '@/components'
import { TextInputForm } from '@/components/form'
import { useGetMe, useUpdateProfile, useUploadAvatar } from '@/domain'
import { IUpdateProfileSchema, useUpdateProfileSchema } from '@/schemas'
import { inputLimits } from '@/constants/inputLimits'
import { useProfileUpdateStore } from '@/store/profileUpdate'
import useOffsets from '@/hooks/useOffsets'

export const EditProfileScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const offsets = useOffsets()
  const [isUpdateInfoModalOpen, setIsUpdateInfoModalOpen] = useState(false)
  const [isChooseMessagingAppModalOpen, setIsChooseMessagingAppModalOpen] =
    useState(false)
  const [isUpdateEmailMethodModalOpen, setIsUpdateEmailMethodModalOpen] =
    useState(false)

  // State for form validity (as in React Native version)
  const [isValidForm, setIsValidForm] = useState(true)

  // Profile update store (as in React Native)
  const { setProfileUpdateFormState } = useProfileUpdateStore()

  // Get user data
  const { data: myProfile, isLoading: isLoadingProfile } = useGetMe()

  // Check verification status (as in React Native version)
  const hasIdentityVerified = myProfile?.verify_state === 'VERIFIED'
  const hasPhoneNumber = myProfile?.phone_number

  // Validation schema
  const schema = useUpdateProfileSchema()

  // Hook for profile update (as in React Native - stay on page)
  const { updateProfile, isLoading: isUpdatingProfile } = useUpdateProfile()

  // Hook for avatar upload (as in React Native - with toast notifications)
  const { mutateAsync: uploadAvatar, isPending: isUploadingAvatar } =
    useUploadAvatar()

  // Form
  const { control, handleSubmit, formState, reset } =
    useForm<IUpdateProfileSchema>({
      resolver: zodResolver(schema),
      defaultValues: {
        firstName: myProfile?.first_name ?? '',
        lastName: myProfile?.last_name ?? '',
        username: myProfile?.username ?? '',
      },
      mode: 'onChange',
    })

  // Update form values when data loads
  useEffect(() => {
    if (myProfile) {
      reset({
        firstName: myProfile.first_name || '',
        lastName: myProfile.last_name || '',
        username: myProfile.username || '',
      })
    }
  }, [myProfile, reset])

  // Track form validity (as in React Native version)
  useEffect(() => {
    setIsValidForm(formState.isValid)
  }, [formState.isValid])

  // Check if form has changes (as in React Native version)
  const isFormReadyToSubmit =
    formState.isDirty && isValidForm && !isUpdatingProfile

  const handleSubmitForm = (formData: IUpdateProfileSchema) => {
    if (!formState.isDirty) {
      // If no changes, just navigate back (as in React Native)
      return
    }

    const updatedFields: Partial<{
      first_name?: string
      last_name?: string
      username?: string
    }> = {}

    // Submit only changed fields (as in React Native version)
    // For verified users, firstName and lastName cannot be changed
    if (formState.dirtyFields.firstName && !hasIdentityVerified) {
      updatedFields.first_name = formData.firstName.trim()
    }
    if (formState.dirtyFields.lastName && !hasIdentityVerified) {
      updatedFields.last_name = formData.lastName.trim()
    }
    if (formState.dirtyFields.username) {
      updatedFields.username = formData.username.trim()
    }

    if (Object.keys(updatedFields).length > 0) {
      updateProfile(updatedFields)
    }
    // If no fields to update, just stay on page (as in React Native)
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  // Function for uploading profile photo
  const handlePhotoUpload = async (imageUrl: string) => {
    try {
      // Convert blob URL to File object
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' })

      await uploadAvatar({ file })
    } catch (error) {
      console.error('Error uploading avatar:', error)
    }
  }

  if (isLoadingProfile) {
    return (
      <BaseView flex={1} center bgColor="background.screen">
        <BaseText color="label.default">Loading...</BaseText>
      </BaseView>
    )
  }

  return (
    <BaseView flex={1} bgColor="background.screen">
      {/* Fixed Header Navigation */}
      <StickyHeaderNavigation
        title={t('form:actions.editProfile')}
        onBackPress={handleGoBack}
      />

      {/* Scrollable Content */}
      <BaseView
        px={12}
        style={{
          position: 'fixed',
          top: `${70 + offsets.top}px`,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: offsets.bottom,
        }}
      >
        {/* Account Section Header */}
        <BaseView mb={24}>
          <BaseText fontSize={18} fontFamily="semibold" color="label.default">
            {t('screens:profile.profileSettings.title')}
          </BaseText>
        </BaseView>

        {/* Profile Photo Section */}
        <BaseView center mb={32}>
          <PhotoInput
            centralize
            size={190} // As in React Native version
            sizeButtonIcon={50}
            iconSize={18}
            uri={myProfile?.avatar_path || ''}
            onSelectImage={handlePhotoUpload}
            isLoading={isUploadingAvatar}
          />
        </BaseView>

        {/* General Section */}
        <BaseView gap={12}>
          <BaseText fontSize={18} fontFamily="semibold" color="label.default">
            {t('translation:general')}
          </BaseText>
          {!hasIdentityVerified && (
            <>
              <TextInputForm
                control={control}
                name="firstName"
                label={t('form:labels.firstName')}
                maxLength={inputLimits.firstName.max}
                autoCapitalize="words"
              />

              <TextInputForm
                control={control}
                name="lastName"
                label={t('form:labels.lastName')}
                maxLength={inputLimits.lastName.max}
                autoCapitalize="words"
              />
            </>
          )}

          <TextInputForm
            control={control}
            name="username"
            label={t('translation:username')}
            maxLength={inputLimits.username.max}
          />

          {/* Phone Field - Read Only (as AnimatedTextInput in RN) */}
          <AnimatedTextInput
            label={t('translation:phone')}
            value={myProfile?.phone_number || ''}
            editable={false}
            iconRight={
              <BaseText fontSize={26} color="#95979B">
                ›
              </BaseText>
            }
            onPress={() => {
              if (!hasPhoneNumber) {
                // TODO: Navigate to add phone
              } else if (hasIdentityVerified) {
                setIsUpdateInfoModalOpen(true)
              } else {
                // Show Choose messaging app modal
                setIsChooseMessagingAppModalOpen(true)
              }
            }}
          />

          {/* Email Field - Read Only (as AnimatedTextInput in RN) */}
          <AnimatedTextInput
            label={t('translation:email')}
            value={myProfile?.email || ''}
            editable={false}
            iconRight={
              <BaseText fontSize={26} color="#95979B">
                ›
              </BaseText>
            }
            onPress={() => {
              if (hasIdentityVerified) {
                setIsUpdateInfoModalOpen(true)
              } else if (!hasPhoneNumber) {
                // TODO: Implement direct email update
              } else {
                // Show Update email method modal
                setIsUpdateEmailMethodModalOpen(true)
              }
            }}
          />

          {/* Save Button - as in React Native version */}
          <BaseView
            style={{
              marginTop: 85,
            }}
          >
            <AppButton
              height={60}
              label={t('form:actions.save')}
              onClick={handleSubmit(handleSubmitForm)}
              disabled={!isFormReadyToSubmit}
              isLoading={isUpdatingProfile}
            />
          </BaseView>
        </BaseView>
      </BaseView>

      {/* Update Info Modal */}
      <UpdateInfoModal
        isOpen={isUpdateInfoModalOpen}
        onClose={() => setIsUpdateInfoModalOpen(false)}
      />

      {/* Choose Messaging App Modal */}
      <ChooseMessagingAppModal
        isOpen={isChooseMessagingAppModalOpen}
        onClose={() => setIsChooseMessagingAppModalOpen(false)}
        onSelect={({ code, img_url }: { code: string; img_url: string }) => {
          // Save selected method to store and navigate to phone update page
          // (exactly as in React Native)
          setProfileUpdateFormState({
            flowType: 'updatePhoneNumber',
            currentPhoneMessagingMethodCode: code as any,
            currentPhoneMessagingMethodImgUrl: img_url,
          })
          navigate('/profile/update-phone-number')
        }}
      />

      {/* Update Email Method Modal */}
      <UpdateEmailMethodModal
        isOpen={isUpdateEmailMethodModalOpen}
        onClose={() => setIsUpdateEmailMethodModalOpen(false)}
        onReceiveCodeByPhone={() => {
          // TODO: Handle receive code by phone
        }}
        onReceiveCodeByEmail={() => {
          // TODO: Handle receive code by email
        }}
      />
    </BaseView>
  )
}
